/**
 * 確認コードでのログインの流れ（SCR-002・ReloginDialog で共通）：email 段 → otp 段（03 6.3 SCR-002）。
 * 確認コードの値は画面の状態にだけ持ち、localStorage・ログ・エラー送信に出さない（要件書 NFR-13）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthVerifyResponse } from '@pcrm/shared';
import { diffSecondsUtc, nowUtc, t } from '@pcrm/shared';
import { issueAuthCode, requestEmailVerification, verifyAuthCode } from '../../../api/endpoints/auth';
import { isApiError } from '../../../api/http-error';
import { IdempotencyKeyHolder } from '../../../lib/idempotency';
import { errorMessageFor } from '../../../lib/messages';

export type LoginStep = 'email' | 'otp';

export interface LoginNotice {
  severity: 'success' | 'info' | 'warning' | 'error';
  text: string;
}

export interface CodeLoginState {
  step: LoginStep;
  email: string;
  notice: LoginNotice | null;
  /** 確認コードの欄の下に出すエラー（AUTH_005 等） */
  code_error: string | null;
  is_email_unverified: boolean;
  resend_wait_seconds: number;
  /** 確認コードの有効期限（API-AUTH-01 の expires_at。ログイン画面の「有効期限: m:ss」に使う：旧PCRM と同じ） */
  code_expires_at: string | null;
  is_sending: boolean;
  is_verifying: boolean;
  is_requesting_verification: boolean;
}

function retryAfterOf(details: unknown): number | null {
  const value = (details as { retry_after_seconds?: unknown } | null)?.retry_after_seconds;
  return typeof value === 'number' ? value : null;
}

export function useCodeLogin(options: { initial_email?: string; onLoggedIn: (result: AuthVerifyResponse) => void }) {
  const { onLoggedIn } = options;
  const [state, setState] = useState<CodeLoginState>({
    step: 'email',
    email: options.initial_email ?? '',
    notice: null,
    code_error: null,
    is_email_unverified: false,
    resend_wait_seconds: 0,
    code_expires_at: null,
    is_sending: false,
    is_verifying: false,
    is_requesting_verification: false,
  });
  const [resend_available_at, setResendAvailableAt] = useState<string | null>(null);
  const verification_key = useRef(new IdempotencyKeyHolder());

  useEffect(() => {
    if (!resend_available_at) {
      return undefined;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil(diffSecondsUtc(nowUtc(), resend_available_at)));
      setState((current) => ({ ...current, resend_wait_seconds: remaining }));
      return remaining;
    };
    if (tick() === 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      if (tick() === 0) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [resend_available_at]);

  /** API-AUTH-01（is_resend のときは失敗の文言が MSG-E-098） */
  const sendCode = useCallback(async (email: string, is_resend = false) => {
    setState((current) => ({
      ...current,
      email,
      is_sending: true,
      notice: null,
      code_error: null,
      is_email_unverified: false,
    }));
    try {
      const result = await issueAuthCode(email);
      setResendAvailableAt(result.resend_available_at);
      setState((current) => ({
        ...current,
        step: 'otp',
        email: result.email,
        code_expires_at: result.expires_at,
        is_sending: false,
        notice: { severity: 'success', text: t('MSG-S-001', { email: result.email }) },
      }));
    } catch (error) {
      const code = isApiError(error) ? error.code : '';
      let text: string;
      if (code === 'MAIL_001') {
        text = t(is_resend ? 'MSG-E-098' : 'MSG-E-099');
      } else if (code === 'RATE_001') {
        const seconds = isApiError(error) ? retryAfterOf(error.details) : null;
        text = seconds ? `${errorMessageFor(error)}（あと ${seconds} 秒）` : errorMessageFor(error);
      } else {
        text = errorMessageFor(error, is_resend ? 'MSG-E-098' : 'MSG-E-099');
      }
      setState((current) => ({
        ...current,
        is_sending: false,
        is_email_unverified: code === 'AUTH_EMAIL_NOT_VERIFIED',
        notice: { severity: code === 'AUTH_EMAIL_NOT_VERIFIED' ? 'warning' : 'error', text },
      }));
    }
  }, []);

  /** API-AUTH-02 */
  const verifyCode = useCallback(
    async (code: string) => {
      setState((current) => ({ ...current, is_verifying: true, code_error: null }));
      try {
        const result = await verifyAuthCode(state.email, code);
        setState((current) => ({ ...current, is_verifying: false }));
        onLoggedIn(result);
      } catch (error) {
        const is_known_error =
          isApiError(error) && ['AUTH_005', 'AUTH_007', 'AUTH_003', 'AUTH_006'].includes(error.code);
        setState((current) => ({
          ...current,
          is_verifying: false,
          code_error: is_known_error ? errorMessageFor(error) : errorMessageFor(error, 'MSG-E-100'),
        }));
      }
    },
    [state.email, onLoggedIn],
  );

  /** API-AUTH-06（Idempotency-Key を付ける：02 2.8） */
  const requestVerification = useCallback(async () => {
    setState((current) => ({ ...current, is_requesting_verification: true }));
    try {
      const body = { email: state.email };
      const result = await requestEmailVerification(state.email, await verification_key.current.keyFor(body));
      verification_key.current.reset();
      setState((current) => ({
        ...current,
        is_requesting_verification: false,
        is_email_unverified: false,
        notice: result.already_verified
          ? { severity: 'info', text: t('FE-MSG-I-015') }
          : { severity: 'success', text: t('FE-MSG-S-001') },
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        is_requesting_verification: false,
        notice: {
          severity: 'error',
          text: isApiError(error) && error.code === 'MAIL_001' ? t('MSG-E-003') : errorMessageFor(error, 'MSG-E-003'),
        },
      }));
    }
  }, [state.email]);

  const backToEmail = useCallback(() => {
    setState((current) => ({ ...current, step: 'email', notice: null, code_error: null, code_expires_at: null }));
  }, []);

  return { state, sendCode, verifyCode, requestVerification, backToEmail };
}
